import React, { useState, useEffect } from 'react';
import { 
  BuildingOffice2Icon,
  UserIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { bedAPI, patientAPI } from '../../api';

const BedManagement = () => {
  const [beds, setBeds] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBed, setSelectedBed] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);

  useEffect(() => {
    loadBeds();
    loadPatients();
  }, []);

  const loadBeds = async () => {
    try {
      setLoading(true);
      const response = await bedAPI.getBeds({
        search: searchQuery,
        status: filterStatus === 'all' ? undefined : filterStatus
      });
      setBeds(response.data?.beds || []);
    } catch (error) {
      console.error('Error loading beds:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const response = await patientAPI.getPatients();
      setPatients(response.data?.patients || []);
    } catch (error) {
      console.error('Error loading patients:', error);
    }
  };

  const handleAssignBed = async (patientId) => {
    if (!selectedBed || !patientId) return;

    try {
      await bedAPI.assignBed(selectedBed._id, { patientId });
      setShowAssignModal(false);
      setSelectedBed(null);
      loadBeds();
      alert('Bed assigned successfully!');
    } catch (error) {
      console.error('Error assigning bed:', error);
      alert('Error assigning bed. Please try again.');
    }
  };

  const handleDischargeBed = async (bedId) => {
    if (!window.confirm('Are you sure you want to discharge this patient?')) return;

    try {
      await bedAPI.dischargeBed(bedId);
      loadBeds();
      alert('Patient discharged successfully!');
    } catch (error) {
      console.error('Error discharging patient:', error);
      alert('Error discharging patient. Please try again.');
    }
  };

  const getBedStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800 border-green-200';
      case 'occupied': return 'bg-red-100 text-red-800 border-red-200';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'reserved': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const BedCard = ({ bed }) => (
    <div className={`
      bg-white rounded-lg shadow-md border-l-4 p-6
      ${bed.status === 'available' ? 'border-l-green-500' :
        bed.status === 'occupied' ? 'border-l-red-500' :
        bed.status === 'maintenance' ? 'border-l-yellow-500' :
        'border-l-blue-500'
      }
    `}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
            <BuildingOffice2Icon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {bed.bedNumber} - {bed.room}
            </h3>
            <p className="text-sm text-gray-500">{bed.ward} Ward</p>
            <p className="text-xs text-gray-400">Type: {bed.bedType}</p>
          </div>
        </div>
        <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getBedStatusColor(bed.status)}`}>
          {bed.status.toUpperCase()}
        </span>
      </div>

      {bed.patient && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <UserIcon className="w-5 h-5 text-gray-600 mr-2" />
              <div>
                <p className="font-medium text-gray-900">
                  {bed.patient.firstName} {bed.patient.lastName}
                </p>
                <p className="text-sm text-gray-500">ID: {bed.patient.externalId}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center text-xs text-gray-500">
                <ClockIcon className="w-3 h-3 mr-1" />
                Admitted: {new Date(bed.admissionDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
        <div>
          <span className="text-gray-600">Daily Rate:</span>
          <span className="ml-1 font-medium">₹{bed.dailyRate}</span>
        </div>
        <div>
          <span className="text-gray-600">Features:</span>
          <span className="ml-1 font-medium">{bed.features?.join(', ') || 'Standard'}</span>
        </div>
      </div>

      <div className="flex space-x-2">
        {bed.status === 'available' && (
          <button
            onClick={() => {
              setSelectedBed(bed);
              setShowAssignModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm flex-1"
          >
            Assign Patient
          </button>
        )}
        {bed.status === 'occupied' && (
          <button
            onClick={() => handleDischargeBed(bed._id)}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm flex-1"
          >
            Discharge Patient
          </button>
        )}
        {bed.status === 'maintenance' && (
          <button
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm flex-1"
          >
            Mark Available
          </button>
        )}
      </div>
    </div>
  );

  const statsData = {
    total: beds.length,
    available: beds.filter(b => b.status === 'available').length,
    occupied: beds.filter(b => b.status === 'occupied').length,
    maintenance: beds.filter(b => b.status === 'maintenance').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bed Management</h1>
          <p className="text-gray-600">Monitor and manage hospital bed allocation</p>
        </div>
        <div className="flex items-center">
          <BuildingOffice2Icon className="w-8 h-8 text-blue-500" />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md border border-blue-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <BuildingOffice2Icon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{statsData.total}</h3>
              <p className="text-sm font-medium text-gray-600">Total Beds</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-green-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{statsData.available}</h3>
              <p className="text-sm font-medium text-gray-600">Available</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-red-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircleIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{statsData.occupied}</h3>
              <p className="text-sm font-medium text-gray-600">Occupied</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-yellow-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{statsData.maintenance}</h3>
              <p className="text-sm font-medium text-gray-600">Maintenance</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search beds..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="maintenance">Maintenance</option>
              <option value="reserved">Reserved</option>
            </select>
          </div>
          
          <div className="text-sm text-gray-600">
            Occupancy Rate: {statsData.total > 0 ? Math.round((statsData.occupied / statsData.total) * 100) : 0}%
          </div>
        </div>
      </div>

      {/* Beds Grid */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Hospital Beds</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading beds...</p>
          </div>
        ) : beds.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {beds.map((bed) => (
              <BedCard key={bed._id} bed={bed} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <BuildingOffice2Icon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Beds Found</h3>
            <p className="text-gray-500">No beds match your current filters.</p>
          </div>
        )}
      </div>

      {/* Assign Patient Modal */}
      {showAssignModal && selectedBed && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Assign Patient to Bed {selectedBed.bedNumber}
                </h3>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedBed(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Patient
                  </label>
                  <select
                    onChange={(e) => handleAssignBed(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    defaultValue=""
                  >
                    <option value="">Choose a patient...</option>
                    {patients.map((patient) => (
                      <option key={patient._id} value={patient._id}>
                        {patient.firstName} {patient.lastName} (ID: {patient.externalId})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedBed(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BedManagement;